import type { CaptureResult, PostHogConfig } from "posthog-js";

/**
 * PostHog init options shared by every couple-facing app in this bundle (/me,
 * the legacy AppRoot shell, /analyze). posthog-js is a global singleton, so
 * whichever app mounts first initialises it — the options MUST be identical
 * everywhere or the privacy posture depends on the landing route.
 *
 * TWIN: portal-www/src/v2/analytics/posthogOptions.ts carries the same posture
 * for the therapist portal. Change both together.
 *
 * Privacy posture (Dova Metrics & KPIs, §7, amended 2026-09-15):
 *   - autocapture OFF: named events only, via each app's typed registry.
 *   - session replay ON, with every text node and input masked.
 *   - person profiles only for identified users; no names or contact details
 *     are ever sent as person properties.
 *   - bearer tokens in URLs (`?token=`, `/esign/<token>`, `sms:` bodies) are
 *     scrubbed from every event, including replay snapshots.
 */

const REDACTED = "[redacted]";

const TOKEN_QUERY = /([?&](?:token|code|access_code)=)[^&#"'\s]*/gi;
const ESIGN_PATH = /\/esign\/[^/?#"'\s]+/g;
const SMS_URI = /\bsms:[^"'\s]*/gi;

export function scrubUrlSecrets(value: string): string {
  if (!value.includes("=") && !value.includes("/esign/") && !value.includes("sms:")) {
    return value;
  }
  return value
    .replace(TOKEN_QUERY, `$1${REDACTED}`)
    .replace(ESIGN_PATH, "/esign/:token")
    .replace(SMS_URI, `sms:${REDACTED}`);
}

/** Walks plain objects/arrays and scrubs every string in place-safe copies. */
function scrubDeep(value: unknown, depth = 0): unknown {
  if (typeof value === "string") return scrubUrlSecrets(value);
  if (depth > 64 || value === null || typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map((v) => scrubDeep(v, depth + 1));
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value)) out[k] = scrubDeep(v, depth + 1);
  return out;
}

export function scrubCaptureResult(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event;
  return {
    ...event,
    properties: scrubDeep(event.properties) as CaptureResult["properties"],
    ...(event.$set ? { $set: scrubDeep(event.$set) as CaptureResult["$set"] } : {}),
    ...(event.$set_once
      ? { $set_once: scrubDeep(event.$set_once) as CaptureResult["$set_once"] }
      : {}),
  };
}

export function buildPostHogOptions(apiHost: string | undefined): Partial<PostHogConfig> {
  return {
    api_host: apiHost,
    ui_host: "https://us.posthog.com",
    defaults: "2025-05-24",
    person_profiles: "identified_only",
    autocapture: false,
    // autocapture only covers clicks/forms — posthog-js still emits its own
    // $pageview on every navigation unless told not to, which would bypass
    // the named-event allowlist and fire before identity is registered.
    capture_pageview: false,
    rageclick: false,
    capture_dead_clicks: false,
    capture_heatmaps: false,
    disable_surveys: true,
    mask_all_text: true,
    mask_all_element_attributes: true,
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "*",
      recordHeaders: false,
      recordBody: false,
      maskCapturedNetworkRequestFn: (request) => ({
        ...request,
        name: scrubUrlSecrets(request.name),
      }),
    },
    cookie_persisted_properties: [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_content",
      "utm_term",
    ],
    before_send: scrubCaptureResult,
  };
}
